import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useI18n } from '@/contexts/I18nContext';
import api from '@/lib/api';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, Legend, AreaChart, Area
} from 'recharts';
import { TrendingUp, Users, FileText, CalendarDays, Building2, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';

const COLORS = ['#007FFF', '#009543', '#CE1021', '#F7D618', '#475569', '#8b5cf6', '#f97316', '#06b6d4'];

const STATUS_COLORS = {
  draft: '#94a3b8',
  submitted: '#F7D618',
  approved: '#009543',
  rejected: '#CE1021',
  correction_needed: '#f97316',
};

export default function Analytics() {
  const { t } = useI18n();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/stats/dashboard')
      .then(r => setStats(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="analytics-loading">
        <div className="w-8 h-8 border-4 border-[#007FFF] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!stats) {
    return <div className="text-center text-muted-foreground py-12" data-testid="analytics-error">{t('common.error')}</div>;
  }

  const statusData = (stats.by_status || []).map(s => ({
    name: t(`status.${s.status}`) || s.status,
    value: s.count,
    fill: STATUS_COLORS[s.status] || '#94a3b8',
  }));

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white border border-border rounded-lg px-3 py-2 shadow-lg">
          <p className="text-xs font-medium text-foreground">{label}</p>
          {payload.map((p, i) => (
            <p key={i} className="text-xs" style={{ color: p.color || p.fill }}>
              {p.name}: <span className="font-semibold">{p.value}</span>
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const exportCSV = async (type) => {
    try {
      const res = await api.get(`/stats/export/${type}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      const disposition = res.headers['content-disposition'];
      const filename = disposition ? disposition.split('filename=')[1] : `export_${type}.csv`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch { }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="analytics-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#007FFF]/10 flex items-center justify-center">
            <TrendingUp className="w-5 h-5 text-[#007FFF]" />
          </div>
          <div>
            <h1 className="text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>{t('analytics.title')}</h1>
            <p className="text-sm text-muted-foreground">{t('analytics.subtitle')}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={() => exportCSV('summary')} data-testid="export-summary-btn">
            <Download className="w-4 h-4 mr-1.5" />{t('analytics.export_summary')}
          </Button>
          <Button variant="outline" size="sm" onClick={() => exportCSV('users')} data-testid="export-users-btn">
            <Download className="w-4 h-4 mr-1.5" />{t('analytics.export_users')}
          </Button>
          <Button variant="outline" size="sm" onClick={() => exportCSV('applications')} data-testid="export-apps-btn">
            <Download className="w-4 h-4 mr-1.5" />{t('analytics.export_apps')}
          </Button>
          <Button variant="outline" size="sm" onClick={() => exportCSV('appointments')} data-testid="export-appts-btn">
            <Download className="w-4 h-4 mr-1.5" />{t('analytics.export_appts')}
          </Button>
        </div>
      </div>

      {/* KPI Row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          { label: t('admin.total_users'), value: stats.total_users, icon: Users, color: '#007FFF', delta: stats.user_trends?.slice(-1)?.[0]?.count || 0 },
          { label: t('admin.apps'), value: stats.total_applications, icon: FileText, color: '#009543', delta: stats.monthly_trends?.slice(-1)?.[0]?.count || 0 },
          { label: t('analytics.appointments'), value: stats.total_appointments || 0, icon: CalendarDays, color: '#F7D618' },
          { label: t('admin.total_emb'), value: stats.total_embassies, icon: Building2, color: '#CE1021' },
        ].map((kpi, i) => {
          const Icon = kpi.icon;
          return (
            <Card key={i} className="border overflow-hidden" data-testid={`kpi-card-${i}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif', color: kpi.color }}>{kpi.value}</p>
                    <p className="text-xs text-muted-foreground mt-1">{kpi.label}</p>
                  </div>
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${kpi.color}10` }}>
                    <Icon className="w-6 h-6" style={{ color: kpi.color }} />
                  </div>
                </div>
                {kpi.delta !== undefined && kpi.delta > 0 && (
                  <p className="text-[11px] text-[#009543] mt-2 font-medium">+{kpi.delta} {t('analytics.this_month')}</p>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Applications Trend */}
        <Card className="border" data-testid="chart-app-trend">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>{t('analytics.app_trend')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={stats.monthly_trends || []}>
                  <defs>
                    <linearGradient id="colorApps" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#007FFF" stopOpacity={0.2} />
                      <stop offset="95%" stopColor="#007FFF" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} stroke="#94a3b8" />
                  <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" allowDecimals={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <Area type="monotone" dataKey="count" stroke="#007FFF" fill="url(#colorApps)" strokeWidth={2} name={t('admin.apps')} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Users Trend */}
        <Card className="border" data-testid="chart-user-trend">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>{t('analytics.user_trend')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={stats.user_trends || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} stroke="#94a3b8" />
                  <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" allowDecimals={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <Line type="monotone" dataKey="count" stroke="#009543" strokeWidth={2} dot={{ r: 4, fill: '#009543' }} name={t('admin.total_users')} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Status Pie */}
        <Card className="border" data-testid="chart-status-pie">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>{t('analytics.by_status')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={statusData} cx="50%" cy="50%" innerRadius={50} outerRadius={90} paddingAngle={3} dataKey="value">
                    {statusData.map((entry, i) => (
                      <Cell key={i} fill={entry.fill} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                  <Legend iconType="circle" wrapperStyle={{ fontSize: '11px' }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* By Country */}
        <Card className="border lg:col-span-2" data-testid="chart-by-country">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>{t('admin.by_country')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stats.by_country || []} layout="vertical" margin={{ left: 80 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis type="number" tick={{ fontSize: 11 }} stroke="#94a3b8" allowDecimals={false} />
                  <YAxis type="category" dataKey="country" tick={{ fontSize: 11 }} stroke="#94a3b8" width={75} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="count" fill="#007FFF" radius={[0, 4, 4, 0]} name={t('admin.apps')} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Appointments by Embassy */}
      {(stats.appointments_by_embassy || []).length > 0 && (
        <Card className="border" data-testid="chart-appts-embassy">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>{t('analytics.appts_by_embassy')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stats.appointments_by_embassy} margin={{ bottom: 60 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="embassy" tick={{ fontSize: 10, angle: -35, textAnchor: 'end' }} stroke="#94a3b8" interval={0} />
                  <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" allowDecimals={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="count" fill="#F7D618" radius={[4, 4, 0, 0]} name={t('analytics.appointments')} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
